import { BrandState } from 'modules/User';
import { isProduction } from 'utils/environment';

const AUTO_DEFAULT_BRAND_API = isProduction ? 'https://autobrand.venngage.prod' : 'https://autobrand.venngage.beer';
const AUTO_BRAND_DOMAIN = window.VenngageE2?.autoBrandDomain || AUTO_DEFAULT_BRAND_API;
const UPLOAD_USER_IMAGE_API = '/brand/upload/logo';
const UPDATE_BRAND_API = '/brand/update';

type FontOrigin = 'google' | 'custom' | 'system' | null;
type VenngageBrandFontType = 'title' | 'subtitle' | 'text';

export interface AutoBrandFont {
  fontFamily: string;
  // matches our brand font types (title, subtitle, text)
  type: VenngageBrandFontType;
  origin: FontOrigin;
  // if we have the font available in the venngage font library it will be true
  isVenngageFont: boolean;
}

export type AutoBrandResponse = {
  colors: number[][];
  logos: string[];
  fonts: AutoBrandFont[];
};

interface UploadBrandLogoResponse {
  status: 'ok' | 'fail';
  data: {
    id: string;
    mediaFile: string;
  };
  message?: string;
}

interface UpdateBrandResponse {
  status: 'ok' | 'fail';
  message?: string;
}

/**
 * Update brand API
 * @param brandRequest
 * @returns UploadBrandLogoResponse
 */
export const updateBrand = async (brandRequest: BrandState) => {
  const response = await fetch(UPDATE_BRAND_API, {
    body: JSON.stringify(brandRequest),
    method: 'post',
  });

  const json = (await response.json()) as UpdateBrandResponse;
  if (!json || json.status !== 'ok') throw new Error(json?.message ?? 'Unknown error');
  return json;
};

/**
 * upload an image to be attached to a brand
 * @param file
 * @param brandId
 * @returns UploadBrandLogoResponse
 */
export const uploadImage = async (file: Blob, brandId: number | String): Promise<UploadBrandLogoResponse> => {
  const formData = new FormData();
  let name = file.type.split('/')[1];
  // Setting filename to '*.png' is required because php upload lib detects mimetype from filename
  formData.append('logo', file, 'logo.' + name);
  formData.append('brandId', String(brandId));

  const response = await fetch(UPLOAD_USER_IMAGE_API, {
    body: formData,
    method: 'post',
  });

  const json = (await response.json()) as UploadBrandLogoResponse;
  if (!json || json.status !== 'ok') throw new Error(json?.message ?? 'Unknown error');
  return json;
};

/**
 * Call Auto brand service to get brand data
 * @param brandURL
 * @returns AutoBrandResponse
 */
export const querySite = (brandURL: string) => {
  return new Promise<AutoBrandResponse>((resolve, reject) => {
    if (!brandURL) {
      reject(new Error('brandURL is invalid'));
      return;
    }

    fetch(`${AUTO_BRAND_DOMAIN}/v2/site?url=${String(brandURL).trim()}`, {
      method: 'GET',
      headers: {
        'Content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'x-requested-with': 'XMLHttpRequest',
      },
    })
      .then(async (httpResponse) => {
        if (!httpResponse.ok) {
          throw new Error('auto brand fail');
        }

        const jsonResponse = httpResponse.json();
        return resolve(jsonResponse);
      })
      .catch((error) => {
        return reject(error);
      });
  });
};
